/* eslint-disable camelcase */
import {
 useEffect, useState, useRef, forwardRef, useImperativeHandle,
} from 'react';
import { nanoid } from 'nanoid';
import { isMobile } from 'react-device-detect';
import { lang, useTranslation, Markdown } from '@/helpers/translate';
import Icon from '@/core/icon';
import { useZusModalStore, ModalWrapper } from '@/helpers/modal.js';
import { useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';
import CalendarSelectableStore from '@/components/calendar/store';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import LazyContentData from '@/components/space/public/events/lazyContentData';
import { useZusHomeStore } from '@/helpers/context';

import {
  Avatar,
  SpacePublicAmbiences,
  SpacePublicBookmark,
  SpacePublicStations,
  SpacePublicHostServices,
  SpacePublicCancelPolicy,
  SpacePublicEquipments,
  SpacePublicEnvironments,
  SpacePublicCalendar,
  SpacePublicGoogle,
  // SpacePublicPrimaryServices,
} from '@/core/dynamic';

import Label from './static/label.js';

function SpacePublicFragment(props) {
  const {
    hostRef, stations, ambiences, contentDescription,
    content, intern_rules, extrahour, isSuperhost, avatar, firstname,
    location, item,
  } = props || {};
  const { spaceId } = useSpacePublicZustandStore.getState();
  const { ua, extWebpJpg: extension } = useZusHomeStore.getState();

  const { t } = useTranslation();
  const { setModal1 } = useZusModalStore.getState();
  const userData = useZusUserConnectedStore((state) => state.userData);
  const { publish } = useSpacePublicZustandStore.getState();

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  function onclickInternRule() {
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1}>
        <section className="flex-col max-w-3xl px-6 pt-10 text-left bg-white min-h-dvh lg:min-h-full lg:rounded-2xl pb-28 lg:py-14 md:p-12 text-purple-5/50 md:flex-row">
          <h2 className="w-full pb-4 border-white-dark2 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:title.rules')}</h2>
          <p className="text-purple-5/70">
            <Markdown>{intern_rules}</Markdown>
          </p>
        </section>
      </ModalWrapper>,
    );
  }

  const copyToClipboard = () => {
    if (isClient) {
      navigator.clipboard.writeText(process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceFR);
    }
  };

  const shareByEmail = () => {
    if (isClient) {
      const subject = encodeURIComponent(`${t('space:share.emailtitle')} ${item.title}`);
      const body = encodeURIComponent(`${t('space:share.email')} ${process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceFR}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  const shareOnWhatsapp = () => {
    if (isClient) {
      const text = encodeURIComponent(`${t('space:share.whatsapp')} ${item.title} - ${process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceFR}`);
      window.location.href = `https://wa.me/?text=${text}`;
    }
  };

  const shareOnLinkedIn = () => {
    if (isClient) {
      window.location.href = `https://www.linkedin.com/sharing/share-offsite/?url=${process.env.NEXT_PUBLIC_URL_V3 + item.urlSpaceFR}`;
    }
  };

  function onClickLocalisation() {
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1}>
        <div className="relative overflow-hidden w-90v h-90v md:w-80v md:h-80v bg-purple-0 rounded-xl">
          <SpacePublicGoogle location={location || null} />
        </div>
      </ModalWrapper>,
    );
  }

  function onclickShare() {
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1}>
        <section className="flex-col max-w-3xl bg-white rounded-2xl p-7 min-w-300 md:p-10 text-purple-5/50 md:flex-row">
          <h2 className="w-full pt-2 pb-3 border-white-dark2 md:pt-0 md:pb-3 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:share.title')}</h2>
          <div className="text-sm">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <button className="flex items-center px-5 py-4 border cursor-pointer border-purple-1 hover:bg-purple-0 hover:border-white-dark" onClick={copyToClipboard}>
                <Icon name="copy" className="w-8 h-8" />
                <span className="ml-3 text-sm text-left text-black md:text-base">{t('space:share.copylink')}</span>
              </button>
              <button className="flex items-center px-5 py-4 border cursor-pointer border-purple-1 hover:bg-purple-0 hover:border-white-dark" onClick={shareByEmail}>
                <Icon name="email_share" className="w-8 h-8" />
                <span className="ml-3 text-sm text-left text-black md:text-base">{t('space:share.emailbtn')}</span>
              </button>
              <button className="flex items-center px-5 py-4 border cursor-pointer border-purple-1 hover:bg-purple-0 hover:border-white-dark" onClick={shareOnWhatsapp}>
                <Icon name="whatsapp" className="w-8 h-8" />
                <span className="ml-3 text-sm text-left text-black md:text-base">WhatsApp</span>
              </button>
              <button className="flex items-center px-5 py-4 border cursor-pointer border-purple-1 hover:bg-purple-0 hover:border-white-dark" onClick={shareOnLinkedIn}>
                <Icon name="linkedin" className="w-8 h-8" />
                <span className="ml-3 text-sm text-left text-black md:text-base">LinkedIn</span>
              </button>
            </div>
          </div>
        </section>
      </ModalWrapper>,
    );
  }

  return (
    <>
      <section className="flex flex-col justify-end text-dark">
        <div className="flex items-center justify-between mt-4 mb-4 md:mb-0 md:mt-8 gap-x-8">
          <div className="items-center hidden md:flex gap-x-8">
            <div className="items-center hidden sm:flex">
              <Avatar
                user={avatar || null}
                className="relative w-16 h-16 mr-3 overflow-hidden rounded-full"
              />
            <div className="text-sm">
              {t('btn:host')}
              <span className="block text-base font-medium text-black">
                {firstname}
              </span>
            </div>
            </div>
            {isSuperhost
              ? (
                <div className="items-center hidden px-2 py-2 rounded-full md:flex">
                  <Label title="search:superhost.title" description="search:superhost.popover" icon="superhost" />
                </div>
              )
              : ''}
          </div>
          <div className="z-10 flex lg:absolute lg:z-0 lg:static top-4 right-4 lg:justify-end lg:items-center gap-x-2 lg:gap-x-3 lg:mr-2">
            {((userData && hostRef === userData.ref) || !publish) || (
            <button
              className="flex items-center pr-4 transition-all duration-300 ease-in-out rounded-lg cursor-pointer group hover:shadow-none text-black-light bg-purple-0 hover:bg-purple/10 hover:text-purple"
              onClick={() => {
                useSpacePublicZustandStore.setState({ hostContactClick: nanoid() });
              }}
            >
              <div className="flex items-center justify-center px-2 py-2 m-1 transition-all duration-300 ease-in-out bg-white rounded-lg group-hover:bg-white">
                <div className="flex items-center justify-center size-6">
                  <Icon className="block transition-all duration-300 ease-in-out fill-current size-5 text-purple-6 group-hover:text-purple" name="bubble" />
                </div>
              </div>
              <span className="ml-1 text-[15px] font-medium text-purple-6 transition-colors duration-300 ease-in-out group-hover:text-purple">{t('btn:contact_host')}</span>
            </button>
            )}
            <div className="items-center justify-center hidden p-2 transition-all duration-300 ease-in-out rounded-lg cursor-pointer md:flex size-12 group bg-purple-0 hover:bg-purple/10 hover:text-purple" onClick={() => onClickLocalisation()}>
              <Icon name="pin" className="w-6 h-6 transition-all duration-300 ease-in-out text-purple-6 group-hover:text-purple" />
            </div>
            <SpacePublicBookmark className="flex items-center justify-center p-2 transition rounded-lg cursor-pointer size-12 bg-purple-0 hover:bg-purple/10 group" iconClassName="stroke-current text-purple-6 hover:text-purple stroke-1.5" {...{ spaceId }} />
            <button className="flex items-center justify-center p-2 transition-all duration-300 ease-in-out rounded-lg cursor-pointer size-12 bg-purple-0 hover:bg-purple/10 group" onClick={() => onclickShare()}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 transition-all duration-300 ease-in-out text-purple-6 group-hover:text-purple">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15m0-3l-3-3m0 0l-3 3m3-3V15" />
              </svg>
            </button>
          </div>
        </div>
        {content.insta ? (
        <div className="flex mb-4 lg:mb-0 lg:mt-8 gap-x-4 lg:my-10">
          <div className="flex items-center w-full py-2 pl-2 rounded-full lg:bg-purple-0 lg:min-w-md pr-7">
            <div className="flex items-center justify-center mr-3 rounded-full bg-purple-0 lg:bg-white size-16 lg:size-10 shrink-0">
              <Icon name="flash" className="h-8 fill-current lg:h-6 text-purple" />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-medium text-black break-words whitespace-normal">{t('search:flash.title')}</span>
              <span className="flex text-sm leading-5 break-words whitespace-normal text-dark">{t('search:flash.popover')}</span>
            </div>
          </div>
        </div>
        ) : ''}
      </section>

      <div itemProp="description">{contentDescription}</div>
      <section className="flex-col mt-8 md:mt-12 text-purple-5/70 md:flex-row">
        <div className="flex gap-x-16">
          {!(content?.vues || []).length || (
            <div>
              <p className="pb-1 text-sm">{t('space:view')}</p>
              <div className="flex flex-wrap gap-2">
                {content.vues.map((v) => <span className="px-2 py-1 text-sm rounded bg-purple/10 text-purple" key={`contentspacepubliccontent.vues.map${v}`}>{t(`space:vues.${v}`)}</span>)}
              </div>
            </div>
          )}
          <div>
            <p className="pb-1 text-sm">Ambiances</p>
            <SpacePublicAmbiences ambiences={ambiences} />
          </div>
        </div>
        <div className="flex flex-col md:flex-row">
          <SpacePublicEnvironments data={content?.hostStuff?.environments || []} />
        </div>
      </section>
      <div>
        <SpacePublicStations stations={stations} />
      </div>
      <SpacePublicCalendar dataByCategory={content} extrahour={extrahour} />
      <div>
        <SpacePublicHostServices services={content?.hostServices || []} />
      </div>
      <div>
        <SpacePublicEquipments equipments={content?.hostStuff?.equipments || []} />
      </div>
      <SpacePublicCancelPolicy policy={content.cancelPolicy} />
      <section className="flex-col mt-12 md:mt-12 text-purple-5/70 md:flex-row">
        <h2 className="w-full pt-8 pb-5 border-t border-white-dark2 md:pt-12 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:deposit')}</h2>
        <div className="flex items-center">
          <div className="px-4 py-2 mr-4 font-medium border rounded-lg text-purple-6 border-purple-1">{t('extra:custom.price', { amount: content.amountDeposit })}</div>
          <button className="px-3 py-2 rounded-lg cursor-pointer text-purple-5 bg-purple-0 hover:bg-purple/10" onClick={() => onclickInternRule()}>{t('space:title.seerules')}</button>
        </div>
      </section>
    </>
  );
}

export default function SpacePublicContent({ spaceRef, ...parentProps }) {
  return (
    <>
      <SpacePublicFragment
        {...parentProps}
        spaceRef={spaceRef}
      />
      <LazyContentData key={`LazyContentData${spaceRef}`} {...{ spaceRef }} />
    </>
  );
}
